import React, { useState } from "react";
import SummaryTable, {
  SummaryTableRow,
} from "@appComponents/SummaryTable/SummaryTable";
import IBankAccountBox from "./IBankAccountBox";
import Flag from "@appComponents/Flag/Flag";
import { displayBankNumber } from "@features/utils/functions";
import { classNames as cn } from "@features/utils/reactFunctions";
import ActionBox from "@appComponents/ActionBox/ActionBox";

import * as elements from "@appStyles/_elements.module.scss";
import * as style from "./BankAccountBoxStyle.module.scss"

const labels: { [key: string]: string } = {
  country: "Bank Account Country",
  bic_swift: "Bank BIC/SWIFT",
  line_1: "Bank Account Address",
  line_2: "Bank Account Address(cont.)",
  postcode: "Bank Account Postcode",
  county: "Bank Account Bank County",
  city: "Bank Account City",
  country_holder: "Account Beneficiary Country",
  line_1_holder: "Account Beneficiary Address",
  line_2_holder: "Account Beneficiary Address(cont.)",
  postcode_holder: "Account Beneficiary Postcode",
  county_holder: "Account Beneficiary County",
  city_holder: "Account Beneficiary City",
  amount: "Amount",
  bic: "BIC/SWIFT",
  iban: "IBAN",
  holder_name: "Account Beneficiary Name",
  bank_address: "Bank Address",
  account_number: "Account Number",
  sort_code: "Bank Sort Code",
  bank_name: "Bank Name",
  aba: "ABA",
  institution_number: "Institution Number",
  branch_code: "Branch Code",
};

const BankAccountBox: React.FunctionComponent<IBankAccountBox> = (props) => {
  const { account, type, setAccountIdToRemove, editAccountClick } = props;
  const [showDetails, setShowDetails] = useState(false);

  const renderActionBox = () => {
    const headlineContent = (
      <>
        <p>{account.name}</p>&nbsp;
        <Flag slug={account.country} />
      </>
    );

    const textContent = (
      <>
        <p>{displayBankNumber(account.iban || account.account_number)}</p>
        {props.label}
      </>
    );

    const handleEditClick = () => {
      if (type === "edit_cancel" || type === "edit") {
        if (editAccountClick) {
          editAccountClick(account);
        }
      } else {
        setShowDetails(true);
      }
    };

    const handleCancelClick = () => {
      if (setAccountIdToRemove) {
        setAccountIdToRemove(account.id);
      }
    };

    return (
      <ActionBox
        key={`account${account.id}`}
        headline={headlineContent}
        text={textContent}
        type={type}
        className={style.boxMobile}
        editClick={handleEditClick}
        cancelClick={handleCancelClick}
      />
    );
  };

  const renderDetails = () => {
    const detailsClass = cn(
      style.details,
      type === "view_cancel" && style.requestedDetails
    );

    const handleToggleDetails = () => {
      setShowDetails(false);
    };

    return (
      <SummaryTable className={style.table}>
        <div className={detailsClass}>
          <div className={style.btnHolder}>
            <button
              onClick={handleToggleDetails}
              className={cn(
                elements.arrowIcon,
                elements.arrowIconBig,
                "fa fa-angle-down"
              )}
            />
          </div>
          {Object.keys(account).map((el, i) => {
            if (el === "currency") return null;
            if (el === "holder_address") {
              return Object.keys(account.holder_address).map((addressEl, j) => {
                return displayRow(
                  account.holder_address[addressEl],
                  labels[addressEl + "_holder"],
                  j
                );
              });
            }

            return displayRow(account[el], labels[el], i);
          })}
          {props.children}
        </div>
      </SummaryTable>
    );
  };

  const displayRow = (
    value: string | number | undefined,
    label: string,
    index: number
  ) =>
    value &&
    label && (
      <SummaryTableRow
        key={index}
        label={label}
        value={formatValue(value, label)}
      />
    );

  const formatValue = (value: string | number, label: string) => {
    switch (label) {
      case "Bank Account Country":
      case "Account Beneficiary Country":
        return (
          <>
            {value} <Flag slug={String(value)} />
          </>
        );
      case "IBAN":
      case "Account Number":
        return displayBankNumber(value.toString());
      case "Currency":
        return String(value).toUpperCase();
      default:
        return value;
    }
  };

  return showDetails ? renderDetails() : renderActionBox();
};

export default BankAccountBox;
